"use client";
import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

//static
const STATIC_PRODUCTS = [
  { id: 1, name: "Product 1", image: "https://cdn.acsdev.in/ManaPrint/1773036660_69ae64748ba0d.jpg" },
  { id: 2, name: "Product 2", image: "https://cdn.acsdev.in/ManaPrint/1768298690_81bejhF8JfL._SY741_.jpg" },
  { id: 3, name: "Product 3", image: "https://cdn.acsdev.in/ManaPrint/magicup.jpg"},
  { id: 4, name: "Product 4", image: "https://cdn.acsdev.in/ManaPrint/tshirtp.jpg"},
  { id: 5, name: "Product 5", image: "https://cdn.acsdev.in/ManaPrint/1773047694_69ae8f8e4ea86.jpg" },
  { id: 6, name: "Product 6", image: "https://cdn.acsdev.in/ManaPrint/keychain.jpg" },
];


// Animated
function AnimatedCounter({ target, duration = 7000, suffix = "", decimals = 0 }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) setIsVisible(true);
      },
      { threshold: 0.5 }
    );
    if (counterRef.current) observer.observe(counterRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    let startTime = null;
    const animate = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(target * easeOutQuart);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return (
    <span ref={counterRef}>
      {decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString()}
      {suffix}
    </span>
  );
}

export default function HeroSection() {
  const navigate = useNavigate();

  const originals = STATIC_PRODUCTS.slice(0, 6);

  const carouselProducts =
    originals.length > 0
      ? [...originals, ...originals, ...originals]
      : [];

  const n = originals.length;

  const [current, setCurrent] = useState(n);

  const containerRef = useRef(null);
  const cardRefs = useRef([]);
  const isJumpingRef = useRef(false);
  const isHoveringRef = useRef(false);
  const autoplayRef = useRef(null);
  const AUTOPLAY_MS = 3000;

  const concaveClip =
    "polygon(0 15%, 10% 7%, 50% 0, 90% 7%, 100% 15%, 100% 85%, 90% 93%, 50% 100%, 10% 93%, 0 85%)";

  useEffect(() => {
    if (n > 0) {
      const target = n;
      silentJump(target);
      setCurrent(target);
    }
  }, [n]);


  useEffect(() => {
    if (!n || carouselProducts.length === 0) return;


    if (current >= n * 2) {
      const jumpTo = current - n;
      silentJump(jumpTo);
      setCurrent(jumpTo);
      return;
    }
    if (current < n) {
      const jumpTo = current + n;
      silentJump(jumpTo);
      setCurrent(jumpTo);
      return;
    }

    // Normal smooth scroll
    const container = containerRef.current;
    const card = cardRefs.current[current];
    if (container && card) {
      const offset =
        card.offsetLeft - (container.clientWidth - card.clientWidth) / 2;
      container.scrollTo({ left: offset, behavior: "smooth" });
    }
  }, [current, n, carouselProducts.length]);

  // Helpers
  function silentJump(idx) {
    const container = containerRef.current;
    const card = cardRefs.current[idx];
    if (!container || !card) return;
    const offset =
      card.offsetLeft - (container.clientWidth - card.clientWidth) / 2;
    container.scrollTo({ left: offset, behavior: "auto" });
  }

  function nextSlide() {
    if (isJumpingRef.current) return;
    setCurrent((prev) => prev + 1);
  }

  function prevSlide() {
    if (isJumpingRef.current) return;
    setCurrent((prev) => prev - 1);
  }

  function handleCardClick(idx) {
    setCurrent(idx);
  }

  // Autoplay
  useEffect(() => {
    if (carouselProducts.length === 0) return;
    autoplayRef.current = setInterval(() => {
      if (!isHoveringRef.current) nextSlide();
    }, AUTOPLAY_MS);
    return () => clearInterval(autoplayRef.current);
  }, [carouselProducts.length]);

  const activeDot = ((current % n) + n) % n;

  return (
    <section className="relative w-full bg-[#fff7ed]">
      <div className="relative">
        <div className="w-full flex justify-center">
          <div className="w-full">
            <div className="relative">

              {/* IMAGE CAROUSEL */}
              <div
                ref={containerRef}
                onMouseEnter={() => (isHoveringRef.current = true)}
                onMouseLeave={() => (isHoveringRef.current = false)}
                className="relative z-0 flex flex-nowrap items-center justify-start overflow-x-auto overflow-y-hidden py-6 pb-[22px] scrollbar-hide mt-8 md:mt-10 lg:mt-14"
                style={{
                  paddingLeft: "clamp(16px, calc(50% - 160px), 300px)",
                  paddingRight: "clamp(16px, calc(50% - 160px), 300px)",
                  WebkitOverflowScrolling: "touch",
                  gap: "24px",
                }}
              >
                {carouselProducts.map((product, idx) => {
                  const isActive = idx === current;
                  const distFromActive = Math.abs(idx - current);
                  const zIndex = 50 - distFromActive;

                  return (
                    <div
                      key={`${product.id}-${idx}`}
                      ref={(el) => (cardRefs.current[idx] = el)}
                      onClick={() => handleCardClick(idx)}
                      className="relative flex-none transition-all duration-300 ease-out cursor-pointer"
                      style={{
                        width: "clamp(200px, 22vw, 380px)",
                        height: "clamp(420px, 48vw, 660px)",
                        marginLeft: "0px",
                        transformOrigin: "center center",
                        zIndex,
                        clipPath: concaveClip,
                        WebkitClipPath: concaveClip,
                        filter: isActive
                          ? "drop-shadow(0 20px 40px rgba(0,0,0,0.25))"
                          : "drop-shadow(0 6px 16px rgba(0,0,0,0.12))",
                        transition: "filter 0.3s ease, z-index 0s",
                      }}
                    >

<div
  className="w-full h-full overflow-hidden flex items-center justify-center relative"
  style={{ backgroundColor: "#ffffff" }}
>
  {/* Blurred background  */}
  <img
    src={product.image || "/placeholder.png"}
    alt=""
    aria-hidden="true"
    className="absolute inset-0 w-full h-full object-cover scale-90"
    style={{
      filter: "blur(18px) brightness(0.7)",
      transform: "scale(1.15)",
    }}
  />
  {/* Main image */}
  <img
    src={product.image || "/placeholder.png"}
    alt={product.name}
    className="relative z-10 w-full h-full object-contain block mt-16"
    style={{
      transition: "transform 0.5s ease, filter 0.4s ease",
      transform: isActive ? "scale(0.98)" : "scale(1.01)",
      filter: isActive ? "brightness(1)" : "brightness(0.62)",
    }}
  />
</div>


                      {isActive && (
                        <div
                          className="absolute left-1/2 -translate-x-1/2 w-14 h-1.5 bg-teal-600 rounded-full"
                          style={{ bottom: "6%", zIndex: 10 }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/*TOP CURVED PANEL */}
              <div className="pointer-events-auto absolute left-0 right-0 top-0 pb-12 z-20">
                <svg
                  viewBox="0 0 1440 420"
                  className="w-full h-[130px] sm:h-[150px] md:h-[240px] lg:h-[220px] xl:h-[260px]"
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient id="g1" x1="0" x2="1">
                      <stop offset="0%" stopColor="#fff7ed" />
                      <stop offset="100%" stopColor="#fff7ed" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,0 L1440,0 L1440,240 C1060,300 820,300 720,310 C620,320 380,320 0,240 Z"
                    fill="url(#g1)"
                  />
                </svg>

                <div className="text-center mt-[-160px] sm:mt-[-170px] md:mt-[-240px] lg:mt-[-310px] px-4">
                 
                   <h1
                    className="text-stone-900 text-xl !tracking-[1px] mt-0 lg:mt-3 sm:text-2xl md:text-3xl lg:text-5xl font-semibold"
                    style={{  fontFamily: "'Great Vibes', cursive", letterSpacing: "0.02em", lineHeight: 1.2 }}
                  >
                    Print What You Imagine{" "}
                    {/* <span className="z-10"
                      style={{
                        background: "linear-gradient(90deg, #fdda22,#fd4e31, #bd0979)", // gradient colors
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        display: "inline",
                      }}
                    >
                      Imagine
                    </span> */}
                  </h1>
                  
                  <h2
                    className="text-teal-800 font-extrabold tracking-[1px] md:tracking-[2px] text-2xl sm:text-2xl md:text-4xl lg:text-6xl drop-shadow-lg"
                    style={{ fontFamily: "Gloock, serif" }}
                  >
                    Create It. Print It. Love It.
                  </h2>

                  {/* Visible on medium screens and up */}
                  <p className="hidden md:block text-stone-900 font-light text-sm lg:text-md leading-relaxed ">
                    Turn your ideas into custom t-shirts, mugs, posters,
                    <br className="hidden lg:block" />
                    gifts, and more — all printed with premium quality.
                  </p>

                  <div className="text-center mt-3 sm:mt-4 mb-8 sm:mb-12 md:mb-20 lg:mb-32">
                    <button
                      onClick={() => navigate("/products")}
                      className="inline-flex items-center gap-2 px-2.5 py-1 sm:px-3 sm:py-1.5 md:px-2.5 md:py-1 lg:px-3 lg:py-1.5 bg-black text-white rounded-full text-xs sm:text-base md:text-sm lg:text-lg hover:bg-stone-800 transition font-mont font-semibold focus:outline-none focus:ring-2 focus:ring-stone-600 focus:ring-offset-2"
                      aria-label="Browse our products"
                    >
                      Browse Products
                      <span className="w-6 h-6 sm:w-7 sm:h-7 md:w-6 md:h-6 lg:w-8 lg:h-8 rounded-full bg-[#434343] flex items-center justify-center">
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-3 md:h-3 lg:w-5 lg:h-5 text-white" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/*  BOTTOM CURVED PANEL */}
              <div className="pointer-events-none absolute left-0 right-0 bottom-0 z-20">
                <svg
                  viewBox="0 0 1440 360"
                  className="w-full h-[160px] md:h-[200px] lg:h-[260px] rotate-180"
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient id="g2" x1="0" x2="1">
                      <stop offset="0%" stopColor="#fff7ed" />
                      <stop offset="100%" stopColor="#fff7ed" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,0 L1440,0 L1440,160 C1060,210 820,210 720,220 C620,230 380,230 0,160 Z"
                    fill="url(#g2)"
                  />
                </svg>
              </div>

              {/* STATS SECTION */}
              <div className="flex absolute left-0 right-0 bottom-2 sm:bottom-4 justify-center z-30 px-4">
                <section
                  className="flex flex-wrap gap-3 sm:gap-5 justify-between max-w-full text-center w-full max-w-[773px] mt-8 sm:mt-12 lg:mt-16"
                  aria-label="Company statistics"
                >
                  <div className="flex flex-col flex-1 min-w-[100px]">
                    <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-none text-neutral-800">
                      <AnimatedCounter target={10000} suffix="+" duration={2500} />
                    </div>
                    <div className="mt-1 sm:mt-2 lg:mt-3 text-xs sm:text-sm leading-loose text-black">
                      Prints Delivered
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 min-w-[100px]">
                    <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-none text-neutral-800">
                      <AnimatedCounter
                        target={4.9}
                        suffix="/5"
                        duration={2500}
                        decimals={1}
                      />
                    </div>
                    <div className="mt-1 sm:mt-2 lg:mt-3.5 text-xs sm:text-sm leading-loose text-black">
                      Customer Rating
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 min-w-[100px]">
                    <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-none text-neutral-800">
                      <AnimatedCounter target={5000} suffix="+" duration={2500} />
                    </div>
                    <div className="mt-1 sm:mt-2 lg:mt-3 text-xs sm:text-sm leading-loose text-black">
                      Happy Customers
                    </div>
                  </div>
                </section>
              </div>

              {/* NAV ARROWS*/}
              {originals.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-teal-600"
                    aria-label="Previous slide"
                  >
                    <svg
                      className="w-6 h-6 text-gray-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-teal-600"
                    aria-label="Next slide"
                  >
                    <svg
                      className="w-6 h-6 text-gray-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
